(function () {
  const DESKTOP_QUERY = '(min-width: 960px)';
  const ROOT_SELECTOR = '.product--multi-beauty-wand-new';
  const CONTAINER_SELECTOR =
    '.product__media-container[data-gallery-style="thumbnails"].above-mobile';

  let resizeObserver = null;
  let mutationObserver = null;
  let toggleObserver = null;

  function getActiveMainMedia(container) {
    return container.querySelector(
      '.product__media .product__media-item:not(.hidden):not(.product__media-item--hide-on-load)'
    );
  }

  function syncMainMediaHeight(container) {
    if (!window.matchMedia(DESKTOP_QUERY).matches) {
      container.style.removeProperty('--mw-main-media-height');
      return;
    }

    const activeItem = getActiveMainMedia(container);
    if (!activeItem) return;

    const height = Math.round(activeItem.getBoundingClientRect().height);
    if (height > 0) {
      container.style.setProperty('--mw-main-media-height', `${height}px`);
    }
  }

  function observeMainMedia(container) {
    if (resizeObserver) resizeObserver.disconnect();
    resizeObserver = new ResizeObserver(() => syncMainMediaHeight(container));

    const activeItem = getActiveMainMedia(container);
    if (activeItem) resizeObserver.observe(activeItem);
  }

  function setup(container) {
    syncMainMediaHeight(container);
    observeMainMedia(container);

    if (mutationObserver) mutationObserver.disconnect();
    mutationObserver = new MutationObserver(() => {
      syncMainMediaHeight(container);
      observeMainMedia(container);
    });

    mutationObserver.observe(container, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-media-item-id']
    });
  }

  function setToggleActiveState(root, mediaId) {
    const buttons = root.querySelectorAll('[data-gallery-target-media-id]');
    buttons.forEach((button) => {
      button.classList.toggle('is-active', button.dataset.galleryTargetMediaId === mediaId);
    });
  }

  function setupMediaQuickToggle(root) {
    const buttons = root.querySelectorAll('[data-gallery-target-media-id]');
    if (!buttons.length) return;

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const mediaId = button.dataset.galleryTargetMediaId;
        if (!mediaId) return;

        const targetThumbnail = root.querySelector(
          `[data-product-thumbnail][data-thumbnail-id="${mediaId}"]`
        );

        if (targetThumbnail) {
          targetThumbnail.click();
        }

        setToggleActiveState(root, mediaId);
      });
    });

    if (toggleObserver) toggleObserver.disconnect();
    toggleObserver = new MutationObserver(() => {
      const activeThumbnail = root.querySelector('[data-product-thumbnail].active');
      if (!activeThumbnail) return;
      const activeMediaId = activeThumbnail.dataset.thumbnailId;
      if (!activeMediaId) return;
      setToggleActiveState(root, activeMediaId);
    });

    toggleObserver.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    const activeThumbnail = root.querySelector('[data-product-thumbnail].active');
    if (activeThumbnail && activeThumbnail.dataset.thumbnailId) {
      setToggleActiveState(root, activeThumbnail.dataset.thumbnailId);
    }
  }

  function setupGalleryNavigation(root) {
    const galleryContainers = root.querySelectorAll(
      '.product__media-container[data-gallery-style="thumbnails"]'
    );

    galleryContainers.forEach((container) => {
      const media = container.querySelector('.product__media');
      if (!media) return;

      const previousButton = container.querySelector('[data-product-gallery-previous]');
      const nextButton = container.querySelector('[data-product-gallery-next]');
      let touchStart = null;

      const getThumbnails = () => Array.from(container.querySelectorAll('[data-product-thumbnail]'));
      const getActiveIndex = (thumbnails) =>
        thumbnails.findIndex((thumbnail) => thumbnail.classList.contains('active'));
      const isActiveContainer = () => {
        const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
        return container.classList.contains('above-mobile') ? isDesktop : !isDesktop;
      };

      const preloadThumbnailMedia = (thumbnail) => {
        const mediaId = thumbnail?.dataset.thumbnailId;
        if (!mediaId) return;

        const image = media.querySelector(`[data-media-item-id="${mediaId}"] img`);
        if (!image || image.complete) return;

        image.loading = 'eager';
        if (typeof image.decode === 'function') {
          image.decode().catch(() => {});
        }
      };

      const preloadAround = (activeIndex) => {
        if (!isActiveContainer() || activeIndex < 0) return;

        const thumbnails = getThumbnails();
        [-1, 1, 2].forEach((offset) => {
          const thumbnail = thumbnails[activeIndex + offset];
          if (thumbnail) preloadThumbnailMedia(thumbnail);
        });
      };

      const updateButtons = () => {
        const thumbnails = getThumbnails();
        const activeIndex = getActiveIndex(thumbnails);
        const isAtStart = activeIndex <= 0;
        const isAtEnd = activeIndex < 0 || activeIndex >= thumbnails.length - 1;

        if (previousButton) {
          previousButton.disabled = isAtStart;
          previousButton.setAttribute('aria-disabled', String(isAtStart));
        }
        if (nextButton) {
          nextButton.disabled = isAtEnd;
          nextButton.setAttribute('aria-disabled', String(isAtEnd));
        }
      };

      const move = (direction) => {
        const thumbnails = getThumbnails();
        const activeIndex = getActiveIndex(thumbnails);
        if (activeIndex < 0) return;

        const target = thumbnails[activeIndex + direction];
        if (!target) return;

        preloadThumbnailMedia(target);
        target.click();
        preloadAround(activeIndex + direction);
        target.closest('.product-thumbnails__item')?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
        window.requestAnimationFrame(updateButtons);
      };

      previousButton?.addEventListener('click', () => move(-1));
      nextButton?.addEventListener('click', () => move(1));

      media.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 1) {
            touchStart = null;
            return;
          }

          touchStart = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY
          };
        },
        { passive: true }
      );

      media.addEventListener(
        'touchend',
        (event) => {
          if (!touchStart || event.changedTouches.length !== 1) return;

          const deltaX = event.changedTouches[0].clientX - touchStart.x;
          const deltaY = event.changedTouches[0].clientY - touchStart.y;
          touchStart = null;

          if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

          if (event.cancelable) event.preventDefault();
          move(deltaX < 0 ? 1 : -1);
        },
        { passive: false }
      );

      const syncNavigation = () => {
        updateButtons();
        preloadAround(getActiveIndex(getThumbnails()));
      };

      const observer = new MutationObserver(syncNavigation);
      const thumbnails = container.querySelector('[data-product-thumbnails]');
      if (thumbnails) {
        observer.observe(thumbnails, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        });
      }

      const desktopMediaQuery = window.matchMedia(DESKTOP_QUERY);
      if (typeof desktopMediaQuery.addEventListener === 'function') {
        desktopMediaQuery.addEventListener('change', syncNavigation);
      } else {
        desktopMediaQuery.addListener(syncNavigation);
      }
      syncNavigation();
    });
  }

  function setupOverviewBundle(root) {
    const bundleElements = root.querySelectorAll('[data-overview-bundle]');
    const giftBundleElements = root.querySelectorAll('[data-overview-gift-bundle]');
    if (!bundleElements.length && !giftBundleElements.length) return;

    const ensureHiddenInput = (form, name) => {
      let input = Array.from(form.querySelectorAll('input')).find(
        (candidate) => candidate.name === name
      );
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }

      return input;
    };

    const removeHiddenInput = (form, name) => {
      const input = Array.from(form.querySelectorAll('input')).find(
        (candidate) => candidate.name === name
      );
      if (input) input.remove();
    };

    const setBundleProductVariantOverride = (form, variantId) => {
      form.querySelectorAll('select[name="id"], input[name="id"]').forEach((input) => {
        if (input.dataset.bundleProductVariantOverride === 'true') return;
        input.disabled = true;
        input.dataset.bundleDisabledProductId = 'true';
      });

      let overrideInput = form.querySelector('input[data-bundle-product-variant-override="true"]');
      if (!overrideInput) {
        overrideInput = document.createElement('input');
        overrideInput.type = 'hidden';
        overrideInput.name = 'id';
        overrideInput.dataset.bundleProductVariantOverride = 'true';
        form.appendChild(overrideInput);
      }
      overrideInput.value = variantId;
    };

    const clearBundleProductVariantOverride = (form) => {
      form.querySelectorAll('[data-bundle-disabled-product-id="true"]').forEach((input) => {
        input.disabled = false;
        input.removeAttribute('data-bundle-disabled-product-id');
      });

      form.querySelectorAll('input[data-bundle-product-variant-override="true"]').forEach((input) => {
        input.remove();
      });
    };

    const getSectionForm = (bundleElement) => {
      const sectionId = bundleElement.dataset.sectionId;
      if (!sectionId) return null;

      return document.querySelector(
        `#shopify-section-${sectionId} form[data-product-form]:not(.visually-hidden)`
      );
    };

    const getProductData = (bundleElement) => {
      const sectionId = bundleElement.dataset.sectionId;
      if (!sectionId) return null;

      const productJsonEl = document.getElementById(`ProductJson-${sectionId}`);
      if (!productJsonEl) return null;

      try {
        return JSON.parse(productJsonEl.textContent);
      } catch (_error) {
        return null;
      }
    };

    let moneyPattern = null;

    const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const buildMoneyPattern = (sampleText, sampleCents) => {
      if (!sampleText || !/\d/.test(sampleText)) return null;

      const sampleNumericMatch = sampleText.match(/-?\d[\d\s.,'’]*/);
      if (!sampleNumericMatch) return null;

      const sampleNumericRaw = sampleNumericMatch[0];
      const sampleNumeric = sampleNumericRaw.trim();
      const sampleNumericWithoutTrailingSpace = sampleNumericRaw.trimEnd();
      const separatorMatches = Array.from(sampleNumeric.matchAll(/[.,]/g));
      const lastSeparatorMatch = separatorMatches[separatorMatches.length - 1];
      const possibleDecimalSeparator = lastSeparatorMatch ? lastSeparatorMatch[0] : '.';
      const possibleDecimalIndex = lastSeparatorMatch ? lastSeparatorMatch.index : -1;
      const expectedValue = Math.abs(Number(sampleCents) || 0) / 100;
      const integerCandidate = Number(sampleNumeric.replace(/[^\d]/g, ''));
      let decimalCandidate = Number.NaN;

      if (possibleDecimalIndex > -1) {
        const candidateInteger = sampleNumeric.slice(0, possibleDecimalIndex).replace(/[^\d]/g, '') || '0';
        const candidateDecimal = sampleNumeric.slice(possibleDecimalIndex + 1).replace(/[^\d]/g, '');
        if (candidateDecimal) {
          decimalCandidate = Number(`${candidateInteger}.${candidateDecimal}`);
        }
      }

      const decimalDistance = Number.isFinite(decimalCandidate)
        ? Math.abs(decimalCandidate - expectedValue)
        : Number.POSITIVE_INFINITY;
      const integerDistance = Number.isFinite(integerCandidate)
        ? Math.abs(integerCandidate - expectedValue)
        : Number.POSITIVE_INFINITY;
      const usesDecimalSeparator = possibleDecimalIndex > -1 && decimalDistance <= integerDistance;
      const decimalSeparator = usesDecimalSeparator ? possibleDecimalSeparator : '.';
      const decimalIndex = usesDecimalSeparator ? possibleDecimalIndex : -1;
      let precision = 0;
      if (decimalIndex > -1) {
        precision = sampleNumeric.slice(decimalIndex + 1).replace(/[^\d]/g, '').length;
      }

      const integerPartRaw = decimalIndex > -1 ? sampleNumeric.slice(0, decimalIndex) : sampleNumeric;
      const thousandSeparatorMatch = integerPartRaw.replace(/^-/, '').match(/[^\d]/);
      const thousandSeparator = thousandSeparatorMatch ? thousandSeparatorMatch[0] : '';

      return {
        precision,
        decimalSeparator,
        thousandSeparator,
        prefix: sampleText.slice(0, sampleNumericMatch.index),
        suffix: sampleText.slice(sampleNumericMatch.index + sampleNumericWithoutTrailingSpace.length),
        sampleCents: Number(sampleCents) || 0
      };
    };

    const formatWithPattern = (cents) => {
      if (!moneyPattern) return null;

      const absolute = Math.abs(Number(cents) || 0) / 100;
      const fixed = absolute.toFixed(moneyPattern.precision);
      let [integerPart, decimalPart] = fixed.split('.');
      if (moneyPattern.thousandSeparator) {
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, moneyPattern.thousandSeparator);
      }

      const decimalChunk =
        moneyPattern.precision > 0
          ? `${moneyPattern.decimalSeparator}${decimalPart || ''.padEnd(moneyPattern.precision, '0')}`
          : '';
      const sign = Number(cents) < 0 ? '-' : '';

      let suffix = moneyPattern.suffix || '';
      if (
        suffix &&
        !/^\s/.test(suffix) &&
        /^[A-Za-z]/.test(suffix)
      ) {
        suffix = ` ${suffix}`;
      }

      return `${moneyPattern.prefix}${sign}${integerPart}${decimalChunk}${suffix}`;
    };

    const refreshMoneyPattern = (bundleElement, variant) => {
      if (!variant) return;

      const singlePriceEl = bundleElement.querySelector(
        '[data-bundle-price="single"], [data-gift-bundle-price]'
      );
      if (!singlePriceEl) return;

      const paidQtyRaw = Number.parseInt(singlePriceEl.dataset.paidQuantity || '1', 10);
      const paidQty = Number.isFinite(paidQtyRaw) && paidQtyRaw > 0 ? paidQtyRaw : 1;
      const sampleCents = Number(variant.price || 0) * paidQty;
      const sampleText = (singlePriceEl.textContent || '').trim();
      const nextPattern = buildMoneyPattern(sampleText, sampleCents);

      if (nextPattern) {
        moneyPattern = nextPattern;
      }
    };

    const formatMoney = (cents) => {
      const patternFormatted = formatWithPattern(cents);
      if (patternFormatted !== null) return patternFormatted;

      const moneyFormat = window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : '${{amount}}';
      const convertedCents = Number(cents) || 0;

      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(convertedCents, moneyFormat);
      }

      const value = convertedCents / 100;
      return `$${value.toFixed(2)}`;
    };

    const getCurrentVariant = (form, productData) => {
      if (!productData || !productData.variants || !productData.variants.length) return null;

      const variantIdInput = form.querySelector('select[name="id"], input[name="id"]');
      const variantId = Number.parseInt(variantIdInput && variantIdInput.value ? variantIdInput.value : '', 10);
      if (Number.isFinite(variantId)) {
        const matchedVariant = productData.variants.find((variant) => variant.id === variantId);
        if (matchedVariant) return matchedVariant;
      }

      const firstAvailable = productData.variants.find((variant) => variant.available);
      return firstAvailable || productData.variants[0];
    };

    const getVariantImageSrc = (variant) => {
      if (!variant) return '';

      if (variant.featured_media && variant.featured_media.preview_image) {
        return variant.featured_media.preview_image.src || variant.featured_media.preview_image;
      }

      if (variant.featured_image) {
        return variant.featured_image.src || variant.featured_image;
      }

      return '';
    };

    const updateBundleThumbnail = (bundleElement, variant) => {
      const nextSrc = getVariantImageSrc(variant);
      if (!nextSrc) return;

      const image = bundleElement.querySelector('.product-overview-bundle__thumb-image');
      if (!image) return;

      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = nextSrc;
    };

    const updateBundlePrices = (bundleElement, variant) => {
      if (!variant) return;

      const unitPrice = Number(variant.price || 0);
      const hasCompareAt = Number(variant.compare_at_price || 0) > unitPrice;
      const referenceUnitPrice = hasCompareAt ? Number(variant.compare_at_price) : unitPrice;
      const discountTemplate = bundleElement.dataset.discountTemplate || '[value]% off';

      bundleElement.querySelectorAll('[data-bundle-price]').forEach((priceEl) => {
        if (priceEl.dataset.staticBundlePrice) {
          priceEl.textContent = formatMoney(Number(priceEl.dataset.staticBundlePrice || 0));
          return;
        }

        const paidQty = Number.parseInt(priceEl.dataset.paidQuantity || '1', 10);
        const total = unitPrice * (Number.isFinite(paidQty) && paidQty > 0 ? paidQty : 1);
        priceEl.textContent = formatMoney(total);
      });

      bundleElement.querySelectorAll('[data-bundle-compare]').forEach((compareEl) => {
        if (compareEl.dataset.staticBundleCompare) {
          const staticCompare = Number(compareEl.dataset.staticBundleCompare || 0);
          const staticPriceEl = bundleElement.querySelector('[data-bundle-price="multi"][data-static-bundle-price]');
          const staticPrice = Number(staticPriceEl && staticPriceEl.dataset.staticBundlePrice ? staticPriceEl.dataset.staticBundlePrice : 0);

          if (staticCompare > staticPrice) {
            compareEl.hidden = false;
            compareEl.textContent = formatMoney(staticCompare);
          } else {
            compareEl.hidden = true;
            compareEl.textContent = '';
          }
          return;
        }

        const compareQty = Number.parseInt(compareEl.dataset.compareQuantity || '1', 10);
        const compareType = compareEl.dataset.bundleCompare;
        const safeCompareQty = Number.isFinite(compareQty) && compareQty > 0 ? compareQty : 1;
        const compareTotal = referenceUnitPrice * safeCompareQty;

        if (compareType === 'single' && !hasCompareAt) {
          compareEl.hidden = true;
          compareEl.textContent = '';
          return;
        }

        compareEl.hidden = false;
        compareEl.textContent = formatMoney(compareTotal);
      });

      const multiPriceEl = bundleElement.querySelector('[data-bundle-price="multi"]');
      const multiCompareEl = bundleElement.querySelector('[data-bundle-compare="multi"]');
      const multiDiscountEl = bundleElement.querySelector('[data-bundle-discount="multi"]');

      if (multiPriceEl && multiCompareEl && multiDiscountEl) {
        if (multiPriceEl.dataset.staticBundlePrice) {
          const staticPrice = Number(multiPriceEl.dataset.staticBundlePrice || 0);
          const staticCompare = Number(multiCompareEl.dataset.staticBundleCompare || 0);

          if (staticCompare > staticPrice) {
            const discountPercent = Math.round(((staticCompare - staticPrice) * 100) / staticCompare);
            multiDiscountEl.hidden = false;
            multiDiscountEl.textContent = discountTemplate.replace('[value]', String(discountPercent));
          } else {
            multiDiscountEl.hidden = true;
            multiDiscountEl.textContent = '';
          }
          return;
        }

        const paidQtyRaw = Number.parseInt(multiPriceEl.dataset.paidQuantity || '1', 10);
        const compareQtyRaw = Number.parseInt(multiCompareEl.dataset.compareQuantity || '1', 10);
        const paidQty = Number.isFinite(paidQtyRaw) && paidQtyRaw > 0 ? paidQtyRaw : 1;
        const compareQty = Number.isFinite(compareQtyRaw) && compareQtyRaw > 0 ? compareQtyRaw : 1;
        const multiPriceTotal = unitPrice * paidQty;
        const multiCompareTotal = referenceUnitPrice * compareQty;

        if (multiCompareTotal > multiPriceTotal) {
          const discountPercent = Math.round(
            ((multiCompareTotal - multiPriceTotal) * 100) / multiCompareTotal
          );
          multiDiscountEl.hidden = false;
          multiDiscountEl.textContent = discountTemplate.replace('[value]', String(discountPercent));
        } else {
          multiDiscountEl.hidden = true;
          multiDiscountEl.textContent = '';
        }
      }
    };

    bundleElements.forEach((bundleElement) => {
      const form = getSectionForm(bundleElement);
      if (!form) return;
      const productData = getProductData(bundleElement);

      const radios = bundleElement.querySelectorAll('[data-bundle-plan-radio]');
      if (!radios.length) return;

      let lastMainVariantId = '';
      const initialVariant = getCurrentVariant(form, productData);
      if (initialVariant && initialVariant.id) {
        lastMainVariantId = String(initialVariant.id);
      }

      const sync = () => {
        const selectedRadio = Array.from(radios).find((radio) => radio.checked) || radios[0];
        const quantity = Number.parseInt(selectedRadio.dataset.quantity || '1', 10);
        const paidQuantityRaw = Number.parseInt(selectedRadio.dataset.paidQuantity || String(quantity), 10);
        const paidQuantity = Number.isFinite(paidQuantityRaw) && paidQuantityRaw > 0 ? paidQuantityRaw : quantity;
        const quantityInput = form.querySelector('input[name="quantity"]');
        const variantIdInput = form.querySelector('select[name="id"], input[name="id"]');
        const isMulti = selectedRadio.value === 'multi';
        const selectedPlanLabel = selectedRadio.dataset.planLabel || selectedRadio.value;
        const giftOfferValue = isMulti ? '1 free device included' : 'No gift included';
        const variant = getCurrentVariant(form, productData);
        const discountTemplate = bundleElement.dataset.discountTemplate || '[value]% off';
        const bundleProductVariantId = selectedRadio.dataset.bundleProductVariantId;
        const bundleProductPrice = Number(selectedRadio.dataset.bundleProductPrice || 0);
        const bundleProductCompare = Number(selectedRadio.dataset.bundleProductCompare || 0);
        const usesBundleProduct = isMulti && bundleProductVariantId;
        const formVariantId = Number.parseInt(variantIdInput && variantIdInput.value ? variantIdInput.value : '', 10);
        const formHasMainVariant =
          Number.isFinite(formVariantId) &&
          productData &&
          productData.variants &&
          productData.variants.some((productVariant) => productVariant.id === formVariantId);

        if (formHasMainVariant) {
          lastMainVariantId = String(formVariantId);
        }

        const lastMainVariant =
          productData &&
          productData.variants &&
          lastMainVariantId
            ? productData.variants.find((productVariant) => String(productVariant.id) === String(lastMainVariantId))
            : null;
        const mainVariant = formHasMainVariant ? variant : lastMainVariant || variant;

        refreshMoneyPattern(bundleElement, mainVariant);

        if (quantityInput && Number.isFinite(quantity) && quantity > 0) {
          quantityInput.value = String(quantity);
        }

        if (usesBundleProduct) {
          setBundleProductVariantOverride(form, bundleProductVariantId);
        } else {
          clearBundleProductVariantOverride(form);
          if (variantIdInput && lastMainVariantId) {
            variantIdInput.value = lastMainVariantId;
          }
        }

        const cards = bundleElement.querySelectorAll('[data-bundle-plan-card]');
        cards.forEach((card) => {
          const cardRadio = card.querySelector('[data-bundle-plan-radio]');
          card.classList.toggle('is-selected', !!(cardRadio && cardRadio.checked));
        });

        ensureHiddenInput(form, 'properties[Bundle plan]').value = selectedPlanLabel;
        ensureHiddenInput(form, 'properties[_Bundle quantity]').value = String(quantity);
        ensureHiddenInput(form, 'properties[_Bundle paid quantity]').value = String(paidQuantity);
        ensureHiddenInput(form, 'properties[Gift offer]').value = giftOfferValue;
        removeHiddenInput(form, 'properties[Bundle quantity]');
        removeHiddenInput(form, 'properties[Bundle paid quantity]');

        updateBundlePrices(bundleElement, mainVariant);

        const buttonPriceEls = form.querySelectorAll('[data-price]');
        if ((mainVariant || usesBundleProduct) && buttonPriceEls.length) {
          const buttonTotal = usesBundleProduct ? bundleProductPrice : Number(mainVariant.price || 0) * paidQuantity;
          buttonPriceEls.forEach((priceEl) => {
            priceEl.textContent = formatMoney(buttonTotal);
          });
        }

        const stickyBarEl = form.querySelector('.sticky-atc-bar');
        if ((mainVariant || usesBundleProduct) && stickyBarEl) {
          const unitPrice = Number(mainVariant.price || 0);
          const hasCompareAt = Number(mainVariant.compare_at_price || 0) > unitPrice;
          const referenceUnitPrice = hasCompareAt ? Number(mainVariant.compare_at_price) : unitPrice;
          const compareQtyRaw = Number.parseInt(selectedRadio.dataset.quantity || String(paidQuantity), 10);
          const compareQty = Number.isFinite(compareQtyRaw) && compareQtyRaw > 0 ? compareQtyRaw : paidQuantity;
          const stickyCompareTotal = usesBundleProduct ? bundleProductCompare : referenceUnitPrice * compareQty;
          const stickyPriceTotal = usesBundleProduct ? bundleProductPrice : unitPrice * paidQuantity;
          const isSinglePlan = selectedRadio.value === 'single';

          const stickyCompareEl = stickyBarEl.querySelector('[data-compare-price]');
          if (stickyCompareEl) {
            if ((isSinglePlan && !hasCompareAt) || (usesBundleProduct && bundleProductCompare <= bundleProductPrice)) {
              stickyCompareEl.textContent = '';
            } else {
              stickyCompareEl.textContent = formatMoney(stickyCompareTotal);
            }
          }

          const stickyDiscountEl = stickyBarEl.querySelector('[data-discount-display]');
          if (stickyDiscountEl) {
            const canShowSingleDiscount = isSinglePlan && hasCompareAt && stickyCompareTotal > stickyPriceTotal;
            const canShowMultiDiscount = !isSinglePlan && stickyCompareTotal > stickyPriceTotal;
            const canShowDiscount = canShowSingleDiscount || canShowMultiDiscount;

            if (canShowDiscount) {
              const discountPercent = Math.round(
                ((stickyCompareTotal - stickyPriceTotal) * 100) / stickyCompareTotal
              );
              stickyDiscountEl.textContent = discountTemplate.replace('[value]', String(discountPercent));
            } else {
              stickyDiscountEl.textContent = '';
            }
          }
        }
      };

      radios.forEach((radio) => {
        radio.addEventListener('change', sync);
      });

      form.addEventListener('submit', sync);
      form.addEventListener('change', sync);
      document.addEventListener('currency:updated', sync);
      document.addEventListener('shopify:currency:change', sync);
      window.addEventListener('pageshow', sync);
      document.addEventListener('change', (event) => {
        const target = event.target;
        if (
          target &&
          target.matches &&
          target.matches('[data-currency-input], select[name="currency_code"], input[name="currency_code"]')
        ) {
          window.setTimeout(sync, 50);
        }
      });

      document.addEventListener('product:variant-change', sync);
      sync();
    });

    giftBundleElements.forEach((bundleElement) => {
      const form = getSectionForm(bundleElement);
      if (!form) return;
      const productData = getProductData(bundleElement);

      const sync = () => {
    const quantityInput = form.querySelector('input[name="quantity"]');
    const variant = getCurrentVariant(form, productData);
    const planLabel = bundleElement.dataset.planLabel || 'Buy 1';
    const giftTexts = Array.from(bundleElement.querySelectorAll('[data-gift-title]'))
      .map((giftEl) => (giftEl.dataset.giftTitle || giftEl.textContent || '').trim())
      .filter(Boolean);

    if (quantityInput) {
      quantityInput.value = '1';
    }

    ensureHiddenInput(form, 'properties[Bundle plan]').value = planLabel;
    ensureHiddenInput(form, 'properties[_Bundle quantity]').value = '1';
    ensureHiddenInput(form, 'properties[_Bundle paid quantity]').value = '1';
    ensureHiddenInput(form, 'properties[Gift offer]').value = giftTexts.join(' + ') || 'Free gifts included';
    removeHiddenInput(form, 'properties[Bundle quantity]');
    removeHiddenInput(form, 'properties[Bundle paid quantity]');

    if (!variant) return;

    refreshMoneyPattern(bundleElement, variant);
    updateBundleThumbnail(bundleElement, variant);

    const priceEl = bundleElement.querySelector('[data-gift-bundle-price]');
    if (priceEl) {
      priceEl.textContent = formatMoney(Number(variant.price || 0));
    }

    const compareAt = Number(variant.compare_at_price || 0);
    const price = Number(variant.price || 0);
    const compareEl = bundleElement.querySelector('[data-gift-bundle-compare]');
    if (compareEl) {
      if (compareAt > price) {
        compareEl.hidden = false;
        compareEl.textContent = formatMoney(compareAt);
      } else {
        compareEl.hidden = true;
        compareEl.textContent = '';
      }
    }

    const discountEl = bundleElement.querySelector('[data-gift-bundle-discount]');
    if (discountEl) {
      if (compareAt > price) {
        const discountPercent = Math.round(((compareAt - price) * 100) / compareAt);
        discountEl.hidden = false;
        discountEl.textContent = `${discountPercent}% off`;
      } else if ((discountEl.textContent || '').trim()) {
        discountEl.hidden = false;
      } else {
        discountEl.hidden = true;
      }
    }

    const buttonPriceEls = form.querySelectorAll('[data-price]');
    buttonPriceEls.forEach((priceNode) => {
      priceNode.textContent = formatMoney(Number(variant.price || 0));
    });
  };

      form.addEventListener('submit', sync);
      form.addEventListener('change', sync);
      document.addEventListener('currency:updated', sync);
      document.addEventListener('shopify:currency:change', sync);
      document.addEventListener('product:variant-change', sync);
      window.addEventListener('pageshow', sync);
      sync();
    });
  }

  function init() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    const container = root.querySelector(CONTAINER_SELECTOR);
    if (!container) return;

    setup(container);
    setupMediaQuickToggle(root);
    setupGalleryNavigation(root);
    setupOverviewBundle(root);

    window.addEventListener('resize', () => syncMainMediaHeight(container));
    window.addEventListener('load', () => syncMainMediaHeight(container));
    document.addEventListener('shopify:section:load', () => syncMainMediaHeight(container));
    document.addEventListener('shopify:section:reorder', () => syncMainMediaHeight(container));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
